import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalDialog,
  ModalClose,
  Typography,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Option,
  Button,
  Stack,
  Checkbox,
  Alert,
} from '@mui/joy';
import {
  materialSpecsApi,
  materialCategoriesApi,
  type MaterialSpecification,
  type MaterialCategory,
} from '../../lib/distributorsApi';
import { useNotification } from '../../contexts/NotificationContext';

interface Props {
  open: boolean;
  onClose: () => void;
  spec: MaterialSpecification | null;
  onSave: () => void;
}

// Units of measure from backend UnitOfMeasure.choices
const UNITS_OF_MEASURE = [
  { value: 'EA', label: 'Each' },
  { value: 'BD', label: 'Bundle' },
  { value: 'SQ', label: 'Square' },
  { value: 'LF', label: 'Linear Foot' },
  { value: 'SF', label: 'Square Foot' },
  { value: 'GAL', label: 'Gallon' },
  { value: 'LB', label: 'Pound' },
  { value: 'BX', label: 'Box' },
  { value: 'CT', label: 'Carton' },
  { value: 'CS', label: 'Case' },
  { value: 'RL', label: 'Roll' },
  { value: 'PC', label: 'Piece' },
  { value: 'PK', label: 'Pack' },
  { value: 'TB', label: 'Tube' },
];

const emptyForm = {
  category: '',
  name: '',
  description: '',
  standard_unit: 'SQ',
  coverage_per_unit: '',
  waste_factor_percent: '10.00',
  calculation_material_type: '',
  default_unit_cost: '',
  is_active: true,
};

export default function MaterialSpecForm({ open, onClose, spec, onSave }: Props) {
  const { showSuccess, showError } = useNotification();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<MaterialCategory[]>([]);
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    if (open) {
      loadCategories();
      if (spec) {
        setFormData({
          category: String(spec.category),
          name: spec.name,
          description: spec.description || '',
          standard_unit: spec.standard_unit,
          coverage_per_unit: spec.coverage_per_unit ?? '',
          waste_factor_percent: spec.waste_factor_percent ?? '10.00',
          calculation_material_type: spec.calculation_material_type || '',
          default_unit_cost: spec.default_unit_cost ?? '',
          is_active: spec.is_active,
        });
      } else {
        setFormData(emptyForm);
      }
      setError(null);
    }
  }, [open, spec]);

  const loadCategories = async () => {
    try {
      const response = await materialCategoriesApi.list({ is_active: true, page: 1 });
      setCategories(response?.results || []);
    } catch {
      setError('Failed to load material categories');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.category) {
      setError('Please select a category');
      return;
    }
    if (!formData.name.trim()) {
      setError('Specification name is required');
      return;
    }
    if (!formData.coverage_per_unit || parseFloat(formData.coverage_per_unit) <= 0) {
      setError('Coverage per unit must be greater than 0');
      return;
    }

    setLoading(true);
    try {
      const payload: Partial<MaterialSpecification> = {
        category: parseInt(formData.category, 10),
        name: formData.name.trim(),
        description: formData.description,
        standard_unit: formData.standard_unit,
        coverage_per_unit: formData.coverage_per_unit,
        waste_factor_percent: formData.waste_factor_percent || '0',
        calculation_material_type: formData.calculation_material_type,
        default_unit_cost: formData.default_unit_cost ? formData.default_unit_cost : null,
        is_active: formData.is_active,
      };

      if (spec) {
        await materialSpecsApi.update(spec.id, payload);
        showSuccess('Material specification updated successfully');
      } else {
        await materialSpecsApi.create(payload);
        showSuccess('Material specification created successfully');
      }
      onSave();
      onClose();
    } catch (err: any) {
      const detail =
        err.response?.data?.detail ||
        err.response?.data?.name?.[0] ||
        err.response?.data?.non_field_errors?.[0] ||
        'Failed to save material specification';
      setError(detail);
      showError(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog sx={{ width: '90%', maxWidth: 560 }}>
        <ModalClose />
        <Typography level="h3" sx={{ mb: 2 }}>
          {spec ? 'Edit Material Specification' : 'Add Material Specification'}
        </Typography>

        <form onSubmit={handleSubmit}>
          <Stack spacing={2}>
            {error && <Alert color="danger">{error}</Alert>}

            <FormControl required>
              <FormLabel>Category</FormLabel>
              <Select
                value={formData.category}
                onChange={(_, value) => setFormData({ ...formData, category: value as string })}
                placeholder="Select a category"
              >
                {categories.map((c) => (
                  <Option key={c.id} value={String(c.id)}>
                    {c.name}
                  </Option>
                ))}
              </Select>
            </FormControl>

            <FormControl required>
              <FormLabel>Name</FormLabel>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Architectural Shingles"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Description</FormLabel>
              <Textarea
                minRows={2}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional description"
              />
            </FormControl>

            <Stack direction="row" spacing={2}>
              <FormControl required sx={{ flex: 1 }}>
                <FormLabel>Standard Unit</FormLabel>
                <Select
                  value={formData.standard_unit}
                  onChange={(_, value) => setFormData({ ...formData, standard_unit: value as string })}
                >
                  {UNITS_OF_MEASURE.map((u) => (
                    <Option key={u.value} value={u.value}>
                      {u.label}
                    </Option>
                  ))}
                </Select>
              </FormControl>

              <FormControl required sx={{ flex: 1 }}>
                <FormLabel>Coverage / Unit</FormLabel>
                <Input
                  type="number"
                  value={formData.coverage_per_unit}
                  onChange={(e) => setFormData({ ...formData, coverage_per_unit: e.target.value })}
                  placeholder="e.g., 100"
                  slotProps={{ input: { min: 0, step: 0.01 } }}
                />
              </FormControl>
            </Stack>

            <Stack direction="row" spacing={2}>
              <FormControl sx={{ flex: 1 }}>
                <FormLabel>Waste Factor (%)</FormLabel>
                <Input
                  type="number"
                  value={formData.waste_factor_percent}
                  onChange={(e) => setFormData({ ...formData, waste_factor_percent: e.target.value })}
                  slotProps={{ input: { min: 0, step: 0.01 } }}
                />
              </FormControl>

              <FormControl sx={{ flex: 1 }}>
                <FormLabel>Default Unit Cost ($)</FormLabel>
                <Input
                  type="number"
                  value={formData.default_unit_cost}
                  onChange={(e) => setFormData({ ...formData, default_unit_cost: e.target.value })}
                  placeholder="Optional"
                  slotProps={{ input: { min: 0, step: 0.01 } }}
                />
              </FormControl>
            </Stack>

            <FormControl>
              <FormLabel>Calculation Material Type</FormLabel>
              <Input
                value={formData.calculation_material_type}
                onChange={(e) => setFormData({ ...formData, calculation_material_type: e.target.value })}
                placeholder="Optional calculation engine identifier"
              />
            </FormControl>

            <FormControl>
              <Checkbox
                label="Active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              />
            </FormControl>

            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              <Button type="submit" color="primary" loading={loading} sx={{ flex: 1 }}>
                {spec ? 'Update' : 'Create'}
              </Button>
              <Button variant="outlined" onClick={onClose} disabled={loading} sx={{ flex: 1 }}>
                Cancel
              </Button>
            </Stack>
          </Stack>
        </form>
      </ModalDialog>
    </Modal>
  );
}
